import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ArrowRight, Lock, Loader2, BookOpen } from "lucide-react";

const CAT_LABEL = {
  contract: "Contract", script: "Script", calculator: "Calculator",
  checklist: "Checklist", vendor: "Vendor Directory", video: "Video",
  glossary: "Glossary",
};

export default function PublicResourceDetail() {
  const { slug } = useParams();
  const [r, setR] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/resources/${slug}`)
      .then((res) => {
        if (res.status === 404) {
          setStatus("not_found");
          return null;
        }
        return res.json();
      })
      .then((d) => {
        if (d) {
          setR(d);
          setStatus("ok");
          document.title = `${d.title} · iSpeedToClose`;
        }
      })
      .catch(() => setStatus("error"));
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-[var(--ink-2)]">
        <Loader2 size={20} className="animate-spin inline mr-2" /> Loading…
      </div>
    );
  }
  if (status === "not_found") {
    return (
      <div className="min-h-screen bg-white">
        <PublicNav />
        <div className="mkt-section text-center py-16">
          <h1 className="mkt-h2">Resource not found</h1>
          <p className="text-[var(--ink-2)] mt-3 mb-6">
            This resource may have moved or is only available to signed-up members.
          </p>
          <Link to="/resources" className="btn btn-primary">Browse all resources</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="public-resource-detail">
      <PublicNav />

      <article className="max-w-3xl mx-auto px-5 py-10">
        <Link to="/resources" className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)] inline-flex items-center gap-1 mb-4">
          <ArrowLeft size={14} /> All resources
        </Link>

        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-100 text-slate-700">
            {CAT_LABEL[r.category] || r.category}
          </span>
          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-800">Free</span>
        </div>

        <h1 className="font-display font-extrabold text-4xl tracking-tight mb-3">{r.title}</h1>
        <p className="text-lg text-[var(--ink-2)] leading-relaxed mb-6">{r.summary}</p>

        {r.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-6">
            {r.tags.map((t) => (
              <span key={t} className="px-2 py-0.5 rounded text-[10px] bg-slate-50 text-slate-600">#{t}</span>
            ))}
          </div>
        )}

        <div className="prose prose-slate max-w-none prose-headings:font-display prose-headings:font-extrabold prose-h2:text-2xl prose-h3:text-lg prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-code:text-rose-600 prose-table:text-sm prose-th:bg-slate-50">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{r.body_md || ""}</ReactMarkdown>
        </div>

        {/* Teaser gate */}
        {r.is_teaser && (
          <div className="mt-8 p-6 rounded-xl border-2 border-dashed border-[var(--primary)]/40 bg-gradient-to-br from-emerald-50 via-white to-amber-50 text-center" data-testid="public-resource-gate">
            <Lock size={28} className="inline mb-2 text-[var(--primary)]" />
            <h2 className="font-display font-extrabold text-2xl mb-2">
              The rest is free — just sign up
            </h2>
            <p className="text-[var(--ink-2)] text-sm max-w-md mx-auto mb-4">
              Get the complete resource plus all 40+ wholesaler templates, scripts,
              calculators, and the live AI Deal Analyzer. No credit card.
            </p>
            <Link to="/register" className="btn btn-primary inline-flex items-center gap-2" data-testid="public-resource-signup-cta">
              Unlock the full library <ArrowRight size={14} />
            </Link>
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-slate-100 text-center">
          <Link to="/resources" className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)] inline-flex items-center gap-1">
            <BookOpen size={12} /> Browse all free resources
          </Link>
        </div>
      </article>
    </div>
  );
}

function PublicNav() {
  return (
    <header className="border-b border-slate-100">
      <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
        <Link to="/" className="font-display font-extrabold text-xl tracking-tight">
          iSpeedToClose
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/resources" className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)]">Resources</Link>
          <Link to="/login" className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)]">Log in</Link>
          <Link to="/register" className="btn btn-primary">Sign up free</Link>
        </div>
      </div>
    </header>
  );
}
